
// Nightlife-themed color utilities and constants
import { cn } from "./utils";

// Role-based color themes
export const roleThemes = {
  'Venue Owner': {
    primary: 'from-purple-600 to-indigo-600',
    accent: 'text-purple-400',
    glow: 'shadow-purple-500/20',
    border: 'border-purple-500/30'
  },
  'Promoter': {
    primary: 'from-pink-600 to-rose-600',
    accent: 'text-pink-400',
    glow: 'shadow-pink-500/20',
    border: 'border-pink-500/30'
  },
  'Artist': {
    primary: 'from-blue-600 to-cyan-600',
    accent: 'text-blue-400',
    glow: 'shadow-blue-500/20',
    border: 'border-blue-500/30'
  },
  'PR Agent': {
    primary: 'from-green-600 to-emerald-600',
    accent: 'text-green-400',
    glow: 'shadow-green-500/20',
    border: 'border-green-500/30'
  },
  'Patron': {
    primary: 'from-violet-600 to-purple-600',
    accent: 'text-violet-400',
    glow: 'shadow-violet-500/20',
    border: 'border-violet-500/30'
  },
  'Admin': {
    primary: 'from-orange-600 to-red-600',
    accent: 'text-orange-400',
    glow: 'shadow-orange-500/20',
    border: 'border-orange-500/30'
  }
};

// Get theme for specific role
export const getRoleTheme = (role: string) => {
  return roleThemes[role as keyof typeof roleThemes] || roleThemes['Patron'];
};

// Gradient text utilities
export const gradientText = (className?: string) => {
  return cn(
    "bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent font-bold tracking-tight",
    className
  );
};

// Premium glassmorphism card styles with enhanced depth
export const glassCard = (className?: string) => {
  return cn(
    "neo-blur backdrop-blur-2xl bg-black/50 border border-white/10 shadow-xl relative",
    className
  );
};

// Role-specific glassmorphism cards
export const roleCard = (role: string, className?: string) => {
  const theme = getRoleTheme(role);
  return cn(
    "neo-blur backdrop-blur-2xl bg-black/50 border shadow-xl relative transition-all duration-500",
    theme.border,
    theme.glow,
    className
  );
};

// Premium section headers with emoji
export const sectionHeaders = {
  upcomingEvents: "✨ Exclusive Events",
  myTickets: "🎟️ Your VIP Access",
  savedEvents: "💎 Curated Selection",
  profile: "👑 Elite Profile",
  recommendations: "🔥 Trending Experiences",
  venueManagement: "🏢 Venue Control",
  promoterTools: "📈 Promotion Hub",
  artistBookings: "🎵 Your Gigs",
  prManagement: "🎯 PR Dashboard",
  adminOverview: "⚡ System Control"
};

// Dynamic empty state messaging with refined personality
export const emptyStateMessages = {
  events: [
    "Your exclusive lineup awaits. Discover your next exceptional experience.",
    "Select from our curated collection of premium events.",
    "Elevate your nightlife with our handpicked selection of events."
  ],
  tickets: [
    "No VIP access yet. Secure your exclusive entry.",
    "Your premium ticket collection awaits its first addition.",
    "Reserve your place at the most coveted venues."
  ],
  saved: [
    "Start building your curated collection of premium events.",
    "Begin crafting your personalized nightlife portfolio.",
    "Your selection of elite experiences is one click away."
  ],
  bookings: [
    "No bookings yet. Start creating unforgettable experiences.",
    "Your next great event is waiting to be booked.",
    "Ready to bring the nightlife to life?"
  ],
  analytics: [
    "Data insights coming soon. Your performance metrics await.",
    "Analytics will appear once you have activity to track.",
    "Build your presence to unlock powerful insights."
  ]
};

// Get a random message from any category
export const getRandomMessage = (category: keyof typeof emptyStateMessages) => {
  const messages = emptyStateMessages[category];
  return messages[Math.floor(Math.random() * messages.length)];
};

// Premium animated button styles with enhanced effects
export const animatedButton = (className?: string) => {
  return cn(
    "relative overflow-hidden group transition-all duration-500",
    "hover:shadow-[0_0_20px_rgba(139,92,246,0.5)]",
    "after:content-[''] after:absolute after:h-full after:w-full",
    "after:top-0 after:left-0 after:bg-gradient-to-r",
    "after:from-purple-500/30 after:to-pink-500/30",
    "after:scale-x-0 after:origin-right after:transition-transform",
    "after:duration-500 hover:after:scale-x-100 hover:after:origin-left",
    "after:-z-10",
    className
  );
};

// Role-specific animated buttons
export const roleButton = (role: string, className?: string) => {
  const theme = getRoleTheme(role);
  return cn(
    "relative overflow-hidden group transition-all duration-500",
    `hover:${theme.glow}`,
    "after:content-[''] after:absolute after:h-full after:w-full",
    "after:top-0 after:left-0 after:bg-gradient-to-r",
    `after:${theme.primary}/30`,
    "after:scale-x-0 after:origin-right after:transition-transform",
    "after:duration-500 hover:after:scale-x-100 hover:after:origin-left",
    "after:-z-10",
    className
  );
};

// Enhanced card highlight effects based on color with glow
export const cardHighlight = (color: string, className?: string) => {
  const highlights = {
    purple: "hover:shadow-[0_10px_25px_-5px_rgba(139,92,246,0.3)] hover:border-purple-500/30",
    pink: "hover:shadow-[0_10px_25px_-5px_rgba(236,72,153,0.3)] hover:border-pink-500/30",
    blue: "hover:shadow-[0_10px_25px_-5px_rgba(59,130,246,0.3)] hover:border-blue-500/30",
    green: "hover:shadow-[0_10px_25px_-5px_rgba(16,185,129,0.3)] hover:border-green-500/30",
    amber: "hover:shadow-[0_10px_25px_-5px_rgba(245,158,11,0.3)] hover:border-amber-500/30",
    orange: "hover:shadow-[0_10px_25px_-5px_rgba(249,115,22,0.3)] hover:border-orange-500/30",
    red: "hover:shadow-[0_10px_25px_-5px_rgba(239,68,68,0.3)] hover:border-red-500/30",
    cyan: "hover:shadow-[0_10px_25px_-5px_rgba(6,182,212,0.3)] hover:border-cyan-500/30",
    indigo: "hover:shadow-[0_10px_25px_-5px_rgba(99,102,241,0.3)] hover:border-indigo-500/30",
    violet: "hover:shadow-[0_10px_25px_-5px_rgba(139,92,246,0.3)] hover:border-violet-500/30",
  };
  
  return cn(
    "transition-all duration-500 hover:shadow-2xl border border-white/5",
    highlights[color as keyof typeof highlights] || highlights.purple,
    className
  );
};

// API simulation utilities
export const simulateApiCall = async <T>(data: T, delay: number = 1000): Promise<T> => {
  await new Promise(resolve => setTimeout(resolve, delay));
  
  // Simulate occasional API errors (5% chance)
  if (Math.random() < 0.05) {
    throw new Error('Simulated API error');
  }
  
  return data;
};

// Loading state utilities
export const loadingStates = {
  loading: "animate-pulse bg-gradient-to-r from-gray-800 to-gray-700",
  error: "bg-red-900/20 border-red-500/30 text-red-400",
  success: "bg-green-900/20 border-green-500/30 text-green-400"
};
